import React, {
  useState,
  useEffect,
  memo,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import { Link, Redirect } from 'react-router-dom';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faFlag } from '@fortawesome/free-regular-svg-icons';
import { Helmet } from 'react-helmet';
import * as yup from 'yup';
import { push } from 'connected-react-router';
import ReactMarkdown from 'react-markdown';
import slugify from 'slugify';
import FormRichTextEditor from '../../FormRichTextEditor/FormRichTextEditor';
import UserImg from '../UserImg/UserImg';
import UploadImageButton from '../UploadImageButton/UploadImageButton';
import { update as updateUser } from 'redux/modules/users';
import { getTopics, topicToPath } from 'redux/modules/topics';
import {
  read as readQuestion,
  update as updateQuestion,
  vote as voteQuestion,
} from 'redux/modules/questions';
import {
  list as listAnswers,
  create as createAnswer,
  vote as voteAnswer,
} from 'redux/modules/answers';
import {
  list as listComments,
  create as createComment,
  vote as voteComment,
} from 'redux/modules/comments';
import { uploadFile } from 'redux/modules/s3';
import { datetimeToText } from '../Forum';
import Images from './Images/Images';
import { card } from '../Forum.module.scss';
import styles from './Question.module.scss';

const CommentComponent = ({
  comment,
  nVotes,
  vote,
  author: { name },
  isQuestionOwner,
  _id,
  onVote,
  disabled,
}) => {
  const handleClick = _ =>
    !disabled ? onVote({ vote: vote === 1 ? 0 : 1, comment_id: _id }) : null;
  // const flag = _ => console.log('Flag was clicked');
  return (
    <div className={styles.comment}>
      <div>
        <div>
          <div className={styles.number}>
            {nVotes > 0 && nVotes}&nbsp;&nbsp;
          </div>
          <div
            className={cx(
              styles.uparrow,
              !vote && styles.unvote,
              disabled && styles.disabled
            )}
            onClick={handleClick}
          ></div>
        </div>
        {/* <FontAwesomeIcon icon={faFlag} onClick={flag} /> */}
      </div>
      <div>
        {comment}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <div className={cx(isQuestionOwner && styles.highlight)}>{name}</div>
      </div>
    </div>
  );
};

CommentComponent.propTypes = {
  comment: PropTypes.string.isRequired,
  nVotes: PropTypes.number.isRequired,
  author: PropTypes.object.isRequired,
  isQuestionOwner: PropTypes.bool.isRequired,
  vote: PropTypes.number.isRequired,
};

CommentComponent.defaultProps = {
  nVotes: 0,
  isQuestionOwner: false,
  vote: false,
  author: {
    name: '',
  },
};

const Comment = memo(CommentComponent);

const Editor = ({ onSubmit, disabled }) => {
  const {
    handleSubmit,
    errors,
    register,
    setValue,
    setError,
    clearError,
    getValues,
    watch,
    formState,
  } = useForm({
    mode: 'onChange',
    validationSchema: yup.object().shape({
      answer: yup.string().required(),
      // images: yup.array().of(
      //   yup.object().shape({
      //     data: yup.string().required(),
      //     name: yup.string().required(),
      //   })
      // ),
    }),
  });
  const placeholder = disabled ? 'Login to submit answer' : '';
  return (
    <form onSubmit={handleSubmit(onSubmit)} key={formState.submitCount}>
      <div className={styles.inputTitle}>Your Answer</div>
      <div className={cx(formState.isSubmitting && styles.loading)}>
        <FormRichTextEditor
          name="answer"
          {...{ register, setValue, errors, placeholder }}
        />
      </div>
      <div className={styles.action}>
        <UploadImageButton
          name="images"
          {...{ register, setValue, setError, clearError, getValues, watch }}
          disabled={disabled || formState.isSubmitting}
        />
        <button
          type="submit"
          className={styles.postButton}
          disabled={formState.isSubmitting || !watch('answer') || disabled}
        >
          POST
        </button>
        {errors.image && (
          <span className={styles.errors}>{errors.image.message}</span>
        )}
      </div>
    </form>
  );
};

Editor.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

Editor.defaultProps = {
  disabled: true,
};

const Vote = ({ nVotes, vote, onVote, disabled }) => {
  const handleUpvote = () =>
    !disabled ? onVote({ vote: vote === 1 ? undefined : 1 }) : null;
  const handleDownvote = () =>
    !disabled ? onVote({ vote: vote === -1 ? undefined : -1 }) : null;
  return (
    <div>
      <div
        className={cx(
          styles.up,
          !(vote === 1) && styles.unvote,
          disabled && styles.disabled
        )}
        onClick={handleUpvote}
      ></div>
      <div className={styles.votes}>{nVotes}</div>
      <div
        className={cx(
          styles.down,
          !(vote === -1) && styles.unvote,
          disabled && styles.disabled
        )}
        onClick={handleDownvote}
      ></div>
    </div>
  );
};

Vote.propTypes = {
  nVotes: PropTypes.number.isRequired,
  vote: PropTypes.number,
  onVote: PropTypes.func.isRequired,
};

Vote.defaultProps = {
  nVotes: 0,
};

const CommentInput = ({ hasComment, onSubmit, onBlur }) => {
  const { register, formState, handleSubmit, watch } = useForm({
    mode: 'onChange ',
    validationSchema: yup.object().shape({
      comment: yup
        .string()
        .required()
        .min(20),
    }),
  });
  const commentLength = watch('comment') && watch('comment').length;
  const handleBlur = _ => (!formState.isValid ? onBlur() : null);
  return (
    <form
      className={cx(styles.inputComment, hasComment && styles.comment)}
      onSubmit={handleSubmit(onSubmit)}
      onBlur={handleBlur}
    >
      <div className={styles.inputDiv}>
        <textarea
          type="textarea"
          autoFocus
          ref={register({ required: true, minlength: 20 })}
          name="comment"
        />
        {commentLength < 20 && <div>{20 - commentLength}/20</div>}
      </div>
      <button
        type="submit"
        disabled={!formState.isValid || formState.isSubmitted}
      >
        Add comment
      </button>
    </form>
  );
};

CommentInput.propTypes = {
  hasComment: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const CardComponent = ({
  acceptedAnswer,
  hasAcceptedAnswer,
  question_id,
  nVotes,
  user,
  onCheck,
  _id,
  text,
  images,
  tags,
  author,
  createdAt,
  comments,
  vote,
  subscribed,
  topic,
  questionOwner,
  isQuestionOwner,
  auth,
  createComment,
  readQuestion,
  voteQuestion,
  voteAnswer,
  voteComment,
  updateUser,
  listComments,
}) => {
  const [showAddComment, setShowAddComment] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const checkAnswer = useCallback(() => onCheck(acceptedAnswer ? null : _id), [
    onCheck,
    acceptedAnswer,
    _id,
  ]);
  const isAnswer = !!question_id;
  const packageData = useCallback(
    value =>
      !!question_id
        ? { ...value, answer: _id, question: question_id, topic }
        : { ...value, question: _id, topic },
    [question_id, _id, topic]
  );
  const handleSubmitComment = useCallback(
    value =>
      createComment(packageData(value)).then(res => {
        setShowAddComment(false);
        return listComments({
          filter: {
            question: res.data.question,
          },
        });
      }),
    [packageData, createComment, listComments]
  );
  const handleVote = useCallback(
    value =>
      question_id
        ? voteAnswer({ ...value, _id })
        : voteQuestion({ ...value, _id }),
    [question_id, _id, voteQuestion, voteAnswer]
  );
  const handleVoteComment = useCallback(
    _id => value => voteComment({ ...value, _id }),
    [voteComment]
  );
  const handleAddComment = useCallback(
    () => setShowAddComment(!showAddComment),
    [showAddComment]
  );
  const handleMenu = useCallback(() => (auth ? setShowMenu(!showMenu) : null), [
    showMenu,
    auth,
  ]);
  const handleSubQuestion = useCallback(
    subscribed => () => {
      setShowMenu(false);
      if (subscribed) {
        updateUser({
          _id: user._id,
          subscribedQuestions: (user.subscribedQuestions || []).filter(
            question => question !== _id
          ),
        }).then(() => readQuestion({ id: _id }));
      } else {
        updateUser({
          _id: user._id,
          subscribedQuestions: [
            ...new Set([...(user.subscribedQuestions || []), _id]),
          ],
        }).then(() => readQuestion({ id: _id }));
      }
    },
    [user, _id, updateUser, readQuestion]
  );
  const CheckButton = ({ className }) =>
    useMemo(
      () => (
        <FontAwesomeIcon
          icon="check"
          onClick={checkAnswer}
          className={cx(isQuestionOwner && styles.clickable, className)}
        />
      ),
      [className]
    );

  const menuRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target))
        setShowMenu(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  return (
    <div
      className={cx(
        card,
        styles.card,
        isAnswer && acceptedAnswer && styles.highlight
      )}
    >
      <div className={styles.arrow}>
        <Vote
          {...{ nVotes, user, vote }}
          onVote={handleVote}
          disabled={!auth}
        />
        {isAnswer &&
          (acceptedAnswer ? (
            <CheckButton className={acceptedAnswer && styles.highlight} />
          ) : (
            isQuestionOwner && !hasAcceptedAnswer && <CheckButton />
          ))}
      </div>
      <div className={styles.main}>
        <div className={styles.text}>
          <ReactMarkdown source={text} />
        </div>
        {images.length > 0 && <Images images={images} />}
        {tags.length > 0 && (
          <div className={styles.tags}>
            {tags.map(tag => (
              <div key={tag}>{tag}</div>
            ))}
          </div>
        )}
        <div className={styles.comments}>
          {comments.length > 0 && (
            <div className={styles.commentList}>
              {comments.map(
                comment =>
                  comment?.author?._id && (
                    <Comment
                      {...comment}
                      key={comment._id}
                      isQuestionOwner={questionOwner === comment.author._id}
                      onVote={handleVoteComment(comment._id)}
                      disabled={!auth}
                    />
                  )
              )}
            </div>
          )}
          {auth &&
            (showAddComment ? (
              <CommentInput
                hasComment={comments.length > 0}
                onSubmit={handleSubmitComment}
                onBlur={handleAddComment}
              />
            ) : (
              <div
                className={cx(
                  styles.add,
                  comments.length > 0 && styles.comment
                )}
                onClick={handleAddComment}
              >
                add a comment
              </div>
            ))}
        </div>
      </div>
      {!isAnswer && (
        <div className={styles.menu} ref={menuRef}>
          <FontAwesomeIcon
            icon="ellipsis-v"
            className={cx(styles.menuButton, !auth && styles.disabled)}
            onClick={handleMenu}
          />
          {showMenu && (
            <button onClick={handleSubQuestion(subscribed)}>
              {subscribed ? 'Uns' : 'S'}ubscribe
            </button>
          )}
        </div>
      )}
      <div className={styles.user}>
        <div className={styles.time}>
          asked {useMemo(() => datetimeToText(createdAt), [createdAt])} ago
        </div>
        {author && (
          <Link className={styles.username} to={`/users/${author._id}/profile/`}>
            <UserImg width={24} alt={author} />
            <span>{author.name}</span>
          </Link>
        )}
      </div>
    </div>
  );
};

CardComponent.propTypes = {
  images: PropTypes.array.isRequired,
  tags: PropTypes.array.isRequired,
  comments: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  auth: PropTypes.bool.isRequired,
};

CardComponent.defaultProps = {
  images: [],
  tags: [],
  comments: [],
  auth: false,
};

const Card = connect(
  ({ entities: { users }, auth: { user } }) => ({ user: users[user] }),
  {
    updateUser,
    readQuestion,
    voteQuestion,
    voteAnswer,
    listComments,
    createComment,
    voteComment,
  }
)(CardComponent);

const Orderby = memo(({ data, onChange, value }) => {
  const handleOrderBy = ({ target }) => onChange(target.innerHTML);
  return (
    <div className={styles.buttons}>
      {data.map(order => (
        <div
          onClick={handleOrderBy}
          key={order}
          className={cx(value === order && styles.highlight)}
        >
          {order}
        </div>
      ))}
    </div>
  );
});

const QuestionComponent = ({
  topics,
  topic,
  _id,
  question,
  description,
  views,
  acceptedAnswer,
  answers,
  createdAt,
  updatedAt,
  author,
  comments,
  tags,
  images,
  nVotes,
  vote,
  subscribed,
  updateQuestion,
  createAnswer,
  listAnswers,
  uploadFile,
  user,
}) => {
  const isQuestionOwner = user === author._id;
  const shareText = useMemo(
    () =>
      encodeURIComponent(`Hey, do you know how to answer this (${question})? `),
    [question]
  );
  const questionUrl = useMemo(
    () => `${window.location.origin}/forum/questions/${_id}`,
    [_id]
  );

  const handleAcceptedAnswer = useCallback(
    acceptedAnswer => {
      if (isQuestionOwner) updateQuestion({ _id, acceptedAnswer });
      else console.log('No permission');
    },
    [isQuestionOwner, _id, updateQuestion]
  );

  const handleAddAnswer = useCallback(
    data => {
      if (!!data?.images?.length) {
        const imagePromises = data.images.map(file =>
          file.file ? uploadFile(file.file) : file.url
        );
        return Promise.all(imagePromises).then(images => {
          return createAnswer({ ...data, images, topic, question: _id }).then(
            res =>
              listAnswers({
                filter: {
                  question: res.data.question,
                },
              })
          );
        });
      }
      return createAnswer({ ...data, topic, question: _id }).then(res =>
        listAnswers({
          filter: {
            question: res.data.question,
          },
        })
      );
    },
    [createAnswer, listAnswers, uploadFile, _id, topic]
  );

  const orders = ['Active', 'Oldest', 'Votes'];
  const [orderBy, setOrderBy] = useState(orders[2]);
  const handleOrderBy = useCallback(value => setOrderBy(value), []);

  const list = useMemo(() => {
    if (orderBy === 'Votes')
      return answers.sort((a, b) => {
        if (a._id === acceptedAnswer) return -1;
        else if (b._id === acceptedAnswer) return 1;
        return b.nVotes - a.nVotes;
      });
    else {
      if (orderBy === 'Active')
        return answers.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      else if (orderBy === 'Oldest')
        return answers.sort(
          (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt)
        );
    }
  }, [orderBy, answers, acceptedAnswer]);

  const handleFacebook = useCallback(() => {
    const link = `facebook.com/sharer/sharer.php?u=${questionUrl}&title=${encodeURIComponent(
      question
    )}`;
    setTimeout(function() {
      window.location = `https://${link}`;
    }, 25);
    window.location = `fb://${link}`;
  }, [questionUrl, question]);

  return (
    <div className={styles.mainContainer}>
      <Helmet>
        <title>{`${question} - Forums`}</title>
      </Helmet>
      <div className={styles.breadcrumbs}>
        <Link to="/forum/all/">All Topics</Link> /{' '}
        <Link to={topicToPath(topic)}>{topics?.[topic]?.name} Discussion</Link>{' '}
        / <span>{question}</span>
      </div>
      <div className={styles.title}>{question}</div>
      <div className={styles.stats}>
        <div>
          asked{' '}
          <span>
            {useMemo(() => datetimeToText(createdAt), [createdAt])} ago
          </span>
        </div>
        <div>
          last{' '}
          <span>
            active {useMemo(() => datetimeToText(updatedAt), [updatedAt])}
          </span>
        </div>
        {/* <div>
          viewed <span>{views} times</span>
        </div> */}
      </div>
      <Card
        text={description}
        {...{
          _id,
          author,
          vote,
          nVotes,
          images,
          tags,
          createdAt,
          comments: comments.filter(comment => !comment.answer),
          subscribed,
          topic,
        }}
        questionOwner={author}
        auth={!!user}
      />
      <div className={styles.mid}>
        <span>{answers.length} Answers</span>
        <Orderby data={orders} onChange={handleOrderBy} value={orderBy} />
      </div>
      {list.map(answer => (
        <Card
          key={answer._id}
          text={answer.answer}
          {...answer}
          comments={comments.filter(comment => comment.answer === answer._id)}
          hasAcceptedAnswer={!!acceptedAnswer}
          acceptedAnswer={acceptedAnswer === answer._id}
          question_id={_id}
          isQuestionOwner={isQuestionOwner}
          questionOwner={author}
          onCheck={handleAcceptedAnswer}
          auth={!!user}
        />
      ))}
      <div className={cx(card, styles.inputCard)}>
        <div className={styles.text}>
          Know someone who can answer? Share a link to this question via&nbsp;
          <a
            href={`whatsapp://send?text=${shareText}${questionUrl}`}
            data-action="share/whatsapp/share"
          >
            WhatsApp
          </a>
          ,&nbsp;
          <a
            href={`https://twitter.com/intent/tweet?text=${shareText}&url=${questionUrl}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Twitter
          </a>
          , or&nbsp;
          <span onClick={handleFacebook}>Facebook</span>.
        </div>
        <Editor onSubmit={handleAddAnswer} disabled={!user} />
      </div>
    </div>
  );
};

QuestionComponent.propTypes = {
  topic: PropTypes.string.isRequired,
  _id: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  views: PropTypes.number.isRequired,
  acceptedAnswer: PropTypes.string,
  answers: PropTypes.array.isRequired,
  comments: PropTypes.array.isRequired,
  createdAt: PropTypes.string.isRequired,
  updatedAt: PropTypes.string.isRequired,
  author: PropTypes.object.isRequired,
  updateQuestion: PropTypes.func.isRequired,
  createAnswer: PropTypes.func.isRequired,
  listAnswers: PropTypes.func.isRequired,
  uploadFile: PropTypes.func.isRequired,
  user: PropTypes.string,
};

QuestionComponent.defaultProps = {
  views: 0,
  answers: [],
  comments: [],
};

const Question = connect(
  ({ auth: { user } }, { topics, answers, comments }) => ({
    user,
    topics,
    answers,
    comments,
  }),
  { updateQuestion, createAnswer, listAnswers, uploadFile }
)(QuestionComponent);

const QuestionPage = ({
  topics,
  question,
  answers,
  comments,
  id,
  name,
  readQuestion,
  listAnswers,
  listComments,
  push,
}) => {
  // const history = useHistory();
  useEffect(() => {
    readQuestion({ id }).catch(_ => push('/forum'));
    listAnswers({
      filter: {
        question: id,
      },
    });
    listComments({
      filter: {
        question: id,
      },
    });
  }, [readQuestion, listAnswers, listComments, id, push]);
  if (question && !name)
    return (
      <Redirect
        to={`/forum/questions/${question._id}/${slugify(question.question)}/`}
      />
    );
  return question && question.author ? (
    <Question
      {...question}
      topics={topics}
      answers={answers}
      comments={comments}
    />
  ) : (
    <div>Loading...</div>
  );
};

QuestionPage.propTypes = {
  readQuestion: PropTypes.func.isRequired,
  listAnswers: PropTypes.func.isRequired,
  listComments: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default connect(
  (state, ownProps) => {
    const { entities } = state;
    const {
      match: {
        params: { id, name },
      },
    } = ownProps;

    const topics = getTopics(state);
    const question = entities.questions &&
      entities.questions[id] && {
        ...entities.questions[id],
        author: entities.users && entities.users[entities.questions[id].author],
      };
    const answers =
      (entities.answers &&
        state.answers.ids &&
        state.answers.ids.map(id => {
          return {
            ...entities.answers[id],
            author:
              entities.users && entities.users[entities.answers[id].author],
          };
        })) ||
      [];
    const comments =
      (entities.comments &&
        state.comments.ids &&
        state.comments.ids.map(id => {
          return {
            ...entities.comments[id],
            author:
              entities.users && entities.users[entities.comments[id].author],
          };
        })) ||
      [];

    return { topics, question, answers, comments, id, name };
  },
  { readQuestion, listAnswers, listComments, push }
)(QuestionPage);
